/**
 * Module dependencies.
 */

import { ReactElement } from 'react';
import { Svg, Type, icons } from 'src/components/core';
import { bytesToSize } from 'src/core/utils/downloads';
import { color, media, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import split from 'lodash/split';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  size: number;
  title: string;
  url: string;
};

/**
 * `Link` styled component.
 */

const Link = styled.a`
  align-items: center;
  border-bottom: 1px solid ${color('grey100')};
  color: ${color('textColor')};
  display: grid;
  grid-column-gap: ${units(2)};
  grid-template-areas:
    'fileTitle   icon'
    'fileOptions icon';
  grid-template-columns: 1fr max-content;
  outline: none;
  padding: ${units(2)} 0;
  text-decoration: none;
  transition: background-color ${theme('animations.defaultTransition')};

  ${media.min('md')`
    grid-template-areas: 'fileTitle fileOptions icon';
    grid-template-columns: max-content 1fr max-content;
  `}

  &:focus,
  &:hover {
    background-color: ${color.transparentize('grey100', 0.1)};
  }
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H5)`
  grid-area: fileTitle;
`;

/**
 * `FileOptions` styled component.
 */

const FileOptions = styled.div`
  color: ${color('grey400')};
  display: flex;
  grid-area: fileOptions;
  grid-gap: ${units(1)};
  text-transform: uppercase;

  ${media.min('md')`
    grid-gap: ${units(2)};
  `}
`;

/**
 * `FileOptionsDetail` styled component.
 */

const FileOptionsDetail = styled(Type.Paragraph)`
  ${media.max('ms')`
    font-size: 12px;
    line-height: 20px;
  `}
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  grid-area: icon;
`;

/**
 * `Item` component.
 */

const Item = ({ className, size, title, url }: Props): ReactElement => {
  const { translate } = useTranslate();
  const fileExtension = split(url, '.').pop();
  const fileSize = bytesToSize(size);

  return (
    <Link
      className={className}
      href={url}
      rel={'noopener'}
      target={'_blank'}
    >
      <Title>{translate(title)}</Title>

      <FileOptions>
        <FileOptionsDetail>{fileExtension}</FileOptionsDetail>

        <FileOptionsDetail>{fileSize}</FileOptionsDetail>
      </FileOptions>

      <Icon
        icon={icons.download}
        size={units(3)}
      />
    </Link>
  );
};

/**
 * Export `Item` component.
 */

export default Item;
